/**
 * Computes the total expected duration for a listening module session.
 *
 * Pre-loads audio metadata (not the full audio data) for every audio item in
 * all sections to retrieve each track's duration, then adds inter-section
 * pauses and the review period.
 */

import { buildMediaSource } from './examSimulation';
import { REVIEW_TIME_SECONDS, LISTENING_INTER_SECTION_PAUSE_SECONDS } from '@/lib/constants';

interface AudioSource {
  url: string | null;
  binary: string | null;
  mimeType: string | null;
}

interface SectionForDuration {
  material_type?: 'audio' | null;
  material_url?: string | null;
  material_binary?: string | null;
  material_mime_type?: string | null;
  questionGroups: Array<{
    context: {
      type: string;
      mediaUrl?: string | null;
      binaryContent?: string | null;
      mimeType?: string | null;
    } | null;
  }>;
}

function loadAudioDuration(src: AudioSource): Promise<number> {
  const mediaSrc = buildMediaSource(src.url, src.binary, src.mimeType, 'audio/mpeg');
  if (!mediaSrc) return Promise.resolve(0);

  return new Promise<number>((resolve) => {
    const audio = new Audio();
    audio.preload = 'metadata';

    const cleanup = (duration: number) => {
      clearTimeout(timeoutId);
      audio.src = '';
      resolve(duration);
    };

    const timeoutId = setTimeout(() => cleanup(0), 8000);

    audio.addEventListener(
      'loadedmetadata',
      () => {
        const d = isFinite(audio.duration) && audio.duration > 0 ? audio.duration : 0;
        cleanup(d);
      },
      { once: true },
    );

    audio.addEventListener('error', () => cleanup(0), { once: true });

    audio.src = mediaSrc;
  });
}

/**
 * Returns the total expected duration (in whole seconds) for the listening module:
 *   Σ audio track durations + (sections − 1) × inter-section pause + review time.
 *
 * Resolves to REVIEW_TIME_SECONDS when there are no audio tracks (graceful
 * fallback for text-only or error scenarios).
 */
export async function computeListeningModuleDuration(
  sections: SectionForDuration[],
): Promise<number> {
  const durationPromises: Promise<number>[] = [];

  for (const section of sections) {
    if (section.material_type === 'audio' && (section.material_url || section.material_binary)) {
      durationPromises.push(
        loadAudioDuration({
          url: section.material_url ?? null,
          binary: section.material_binary ?? null,
          mimeType: section.material_mime_type ?? null,
        }),
      );
    }

    for (const group of section.questionGroups) {
      if (
        group.context?.type === 'audio' &&
        (group.context.mediaUrl || group.context.binaryContent)
      ) {
        durationPromises.push(
          loadAudioDuration({
            url: group.context.mediaUrl ?? null,
            binary: group.context.binaryContent ?? null,
            mimeType: group.context.mimeType ?? null,
          }),
        );
      }
    }
  }

  const durations = await Promise.all(durationPromises);
  const audioDurationSum = durations.reduce((sum, d) => sum + d, 0);
  const interSectionPauses = Math.max(0, sections.length - 1) * LISTENING_INTER_SECTION_PAUSE_SECONDS;

  return Math.ceil(audioDurationSum + interSectionPauses) + REVIEW_TIME_SECONDS;
}
